import * as THREE from 'three';
import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import { RENDER_LAYER } from './LayeredRenderer';
import { PlanetRingSystem } from './PlanetRingSystem';
import {
  GLSL_CLOUD_DETAIL,
  GLSL_CLOUD_FIELD,
  GLSL_CONSTANTS,
  GLSL_DITHER,
  GLSL_NOISE,
  GLSL_RING_DENSITY,
} from './shaders/common.glsl';
import { MIN_SCALE_HEIGHT, SUN_COLOR, SUN_INTENSITY_DEFAULT } from './shaders/atmosphereDefaults';

/**
 * Sample count for the near-field slab march (see the file comment below).
 * The expensive macro field is sampled once regardless; this only prices the
 * cheap per-step detail term, so it scales with quality. Interpolated into
 * the fragment shader as the `for` loop's compile-time bound — see
 * AtmosphereMaterial for why that has to be a constant rather than
 * `uMarchSteps` itself — so raising MARCH_STEPS_HIGH above this without also
 * raising MAX_MARCH_STEPS would silently truncate the march instead of doing
 * what the number says.
 */
const MAX_MARCH_STEPS = 16;
const MARCH_STEPS_HIGH = 14;
const MARCH_STEPS_MEDIUM = 8;
const MARCH_STEPS_LOW = 4;

/**
 * The deck's inner (lit) boundary sits at least this far above whatever solid
 * surface the body presents (its waterline, or its datum on a dry world), so
 * the slab's floor never coincides with — or dips below — the ground itself.
 */
const MIN_INNER_CLEARANCE = 50;

/**
 * Opaque lower atmosphere for gas giants.
 *
 * A gas giant has no terrain, but that does not make it transparent: below the
 * visible weather deck the optical depth becomes enormous. The procedural
 * cloud shell remains alpha blended on top of this sphere, so clear lanes show
 * darker gas instead of the galaxy background.
 */
const GAS_GIANT_INTERIOR_VERTEX = /* glsl */ `
varying vec3 vUp;

void main() {
  vUp = normalize(mat3(modelMatrix) * normal);
  vec4 world = modelMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const GAS_GIANT_INTERIOR_FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_RING_DENSITY}

varying vec3 vUp;

uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform float uSunIntensity;
uniform vec3 uDeepColor;
uniform float uPlanetRadius;
uniform vec3 uRingNormal;
uniform float uRingInnerRadius;
uniform float uRingOuterRadius;
uniform float uRingOpacity;

void main() {
  vec3 up = normalize(vUp);
  vec3 L = normalize(uSunDirection);
  float sunUp = dot(up, L);
  float daylight = smoothstep(-0.18, 0.14, sunUp);
  float diffuse = 0.16 + 0.84 * max(sunUp, 0.0);
  float ringShadow = ringShadowAtPoint(
    up * uPlanetRadius,
    L,
    uRingNormal,
    uRingInnerRadius,
    uRingOuterRadius,
    uRingOpacity
  );
  vec3 colour = uDeepColor * uSunColor * uSunIntensity
    * diffuse * daylight * (1.0 - ringShadow);
  gl_FragColor = vec4(colour, 1.0);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

// CPU twin of the shader's hash/value-noise path. This is intentionally the
// same field rather than a second "near cloud" heuristic: it is sampled once
// per frame at the camera and tells the shader whether the observer is inside
// a dense part of the same global weather map drawn in the sky.
const fract = (v: number): number => v - Math.floor(v);
const mixNumber = (a: number, b: number, t: number): number => a + (b - a) * t;
const clampUnit = (v: number): number => Math.max(0, Math.min(1, v));
const smoothstepNumber = (edge0: number, edge1: number, x: number): number => {
  const t = clampUnit((x - edge0) / Math.max(1e-9, edge1 - edge0));
  return t * t * (3 - 2 * t);
};

function cloudHash13(x: number, y: number, z: number): number {
  x = fract(x * 0.1031);
  y = fract(y * 0.1031);
  z = fract(z * 0.1031);
  const d = x * (z + 31.32) + y * (y + 31.32) + z * (x + 31.32);
  x += d;
  y += d;
  z += d;
  return fract((x + y) * z);
}

function cloudValueNoise(x: number, y: number, z: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iz = Math.floor(z);
  let fx = fract(x);
  let fy = fract(y);
  let fz = fract(z);
  fx = fx * fx * (3 - 2 * fx);
  fy = fy * fy * (3 - 2 * fy);
  fz = fz * fz * (3 - 2 * fz);
  const z0 = mixNumber(
    mixNumber(cloudHash13(ix, iy, iz), cloudHash13(ix + 1, iy, iz), fx),
    mixNumber(cloudHash13(ix, iy + 1, iz), cloudHash13(ix + 1, iy + 1, iz), fx),
    fy,
  );
  const z1 = mixNumber(
    mixNumber(cloudHash13(ix, iy, iz + 1), cloudHash13(ix + 1, iy, iz + 1), fx),
    mixNumber(cloudHash13(ix, iy + 1, iz + 1), cloudHash13(ix + 1, iy + 1, iz + 1), fx),
    fy,
  );
  return mixNumber(z0, z1, fz);
}

function cloudFbm(x: number, y: number, z: number, octaves: number): number {
  let sum = 0;
  let norm = 0;
  let amplitude = 0.5;
  for (let i = 0; i < octaves; i++) {
    sum += cloudValueNoise(x, y, z) * amplitude;
    norm += amplitude;
    x *= 2.03;
    y *= 2.03;
    z *= 2.03;
    amplitude *= 0.5;
  }
  return norm > 0 ? sum / norm : 0;
}

/**
 * CPU twin of cloudLobeScaleAt in the FRAGMENT shader — see that comment.
 *
 * Kept as a function (rather than inlining the constant at each call site)
 * so a future, non-swirling variety mechanism has one place to live.
 */
function cloudLobeScaleAt(_x: number, _y: number, _z: number, _windX: number, _windZ: number): number {
  return 1.0;
}

/** CPU twin of cloudErosionAt in the FRAGMENT shader — see that comment. */
function cloudErosionAt(
  x: number,
  y: number,
  z: number,
  windX: number,
  windZ: number,
  marchMacro: number,
  lobeScale: number,
): number {
  const px = x + windX;
  const pz = z + windZ;
  const erosion = cloudFbm(px / (1150 * lobeScale), y / (1150 * lobeScale), pz / (1150 * lobeScale), 2);
  const micro = cloudFbm(px / 260, y / 260, pz / 260, 1);
  const combined = mixNumber(erosion, micro, 0.28) - 0.5;
  return combined > 0 ? combined * marchMacro : combined;
}

function globalCloudDensityAt(
  local: Vec3,
  time: number,
  drift: number,
  coverage: number,
  banded: number,
): { density: number; detail: number } {
  const px = local.x * 3 + time * drift * 3;
  const py = local.y * 3;
  const pz = local.z * 3 + time * drift * 1.8;
  const wx = cloudFbm(px * 1.7, py * 1.7, pz * 1.7, 3) - 0.5;
  const wy = cloudFbm(px * 1.7 + 17, py * 1.7 + 17, pz * 1.7 + 17, 3) - 0.5;
  const wz = cloudFbm(px * 1.7 + 43, py * 1.7 + 43, pz * 1.7 + 43, 3) - 0.5;
  // Softened from 1.4: the warp is deliberately what turns bland blobs into
  // fronts and streaks (see the GLSL twin's comment), but at Meridia's
  // near-total coverage that streak direction had nothing else in the field
  // to compete with it and read as combed lines rather than clouds.
  const qx = px + wx * 0.55;
  const qy = py + wy * 0.55;
  const qz = pz + wz * 0.55;
  const synoptic = cloudFbm(
    qx * 0.38 + 31.7,
    qy * 0.38 - 18.2,
    qz * 0.38 + 5.4,
    5,
  );
  const broad = cloudFbm(qx * 0.92, qy * 0.92, qz * 0.92, 5);
  const mesoscale = cloudFbm(qx * 2.7 + 9.7, qy * 2.7 - 4.3, qz * 2.7 + 13.1, 4);
  const detail = cloudFbm(qx * 7.2 - 11.4, qy * 7.2 + 6.8, qz * 7.2 + 3.2, 4);
  const bands = 0.5 + 0.5 * Math.sin(local.y * 22 + cloudFbm(qx * 2, qy * 2, qz * 2, 3) * 3);
  const weather = mixNumber(broad, mesoscale, 0.24) + (synoptic - 0.46) * 0.7;
  const banded_ = mixNumber(weather, bands * 0.72 + weather * 0.28, banded);
  // A second, independent layer of noise with the axes cycled (so its own
  // warp-driven streaks run a different way) blended in at low weight. Breaks
  // up the single dominant streak direction the warp alone produces, without
  // erasing it — the field still reads as fronts, just no longer as combed
  // parallel lines.
  const crossGrain = cloudFbm(qz * 1.35 + 51.0, qx * 1.35 + 7.0, qy * 1.35 + 22.0, 4);
  const field = mixNumber(banded_, crossGrain, 0.4);
  const threshold = 1 - coverage;
  const density = smoothstepNumber(threshold - 0.065, threshold + 0.085, field);
  return {
    density: clampUnit(Math.pow(density, 1.2) * mixNumber(0.62, 1.28, detail)),
    detail,
  };
}

/**
 * Procedural cloud deck.
 *
 * A thin shell at the body's cloud altitude, drawn from both sides so it works
 * overhead from the ground and underfoot from orbit. Coverage, altitude and
 * tint come from the body definition, so Halcyon gets broken water cloud,
 * Ashkar a total sulphurous overcast and Kharis the occasional dust band —
 * without a per-planet texture anywhere.
 *
 * The deck is parented to a group carrying the planet's rotation, so the noise
 * is evaluated in planet-fixed coordinates. That is what makes clouds sit over
 * the same ground as the planet turns instead of sliding across it, and it also
 * means the slow drift applied here reads as weather rather than as the whole
 * sky rotating.
 *
 * The macro shape (`cloudDensityAt`, shared with the terrain's shadow lookup)
 * is sampled in ANGULAR coordinates on the unit sphere, which is right for a
 * weather pattern that has to look the same whether you are on the ground or
 * in orbit. It is wrong on its own for the ground: the camera sits a few
 * kilometres below a shell whose radius is thousands of kilometres, so the
 * parallax between the zenith and the horizon is a small fraction of a
 * degree, and the whole visible sky dome ends up sampling almost one point of
 * that field — a flat, textureless wash, not clouds. So the deck also has real
 * depth: a short ray march through a thin slab, adding a metre-scale detail
 * term (`cloudDetailAt`) that varies with actual distance travelled, which a
 * grazing ray has plenty of even when it barely moves angularly. The macro
 * field is still sampled only once per fragment — it does not change enough
 * across the slab's thickness to be worth resampling — so the march only
 * pays for the cheap per-step detail term.
 *
 * Lighting stays deliberately simple even with the march. A cloud deck seen
 * from below is dominated by transmitted light and from above by reflected
 * light, and the interesting part is the terminator, where a low sun lights
 * the tops and leaves the undersides dark. That much is one dot product,
 * computed once per fragment rather than per sample.
 */

const VERTEX = /* glsl */ `
varying vec3 vLocal;
varying vec3 vRenderPosition;

void main() {
  // Preserve the raw proxy-triangle position. Perspective-correct
  // interpolation then produces the exact camera ray in both spaces; a
  // direction normalised per vertex exposes every triangle to metric noise.
  vLocal = position;
  vec4 world = modelMatrix * vec4(position, 1.0);
  vRenderPosition = world.xyz;
  vec4 clip = projectionMatrix * viewMatrix * world;
  // The shell is only a raster launcher; the fragment shader computes and
  // writes the real cloud depth. Keep its XY/W projection but place it inside
  // every depth camera's clip volume. Otherwise the near/mid/far cameras clip
  // different proxy triangles before the real slab can choose its owner,
  // exposing long triangular seams at the pass handovers.
  gl_Position = vec4(clip.xy, 0.0, clip.w);
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_NOISE}
${GLSL_CLOUD_FIELD}
${GLSL_DITHER}
${GLSL_CLOUD_DETAIL}
${GLSL_RING_DENSITY}

varying vec3 vLocal;
varying vec3 vRenderPosition;

uniform vec3 uPlanetCentre;
uniform vec3 uCameraFixed;
uniform float uProxyRadius;
uniform float uPlanetRadius;
uniform float uOccluderRadius;
uniform float uInnerRadius;
uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform float uSunIntensity;
uniform vec3 uCloudColor;
uniform float uCoverage;
uniform float uTime;
uniform float uDrift;
uniform float uBanded;
/** Cross-fade with the body's distant impostor, which draws its own clouds. */
uniform float uLayerFade;
uniform float uDeckRadius;
uniform float uDeckThickness;
uniform float uMarchSteps;
uniform float uScaleHeight;
uniform vec3 uRayleigh;
uniform float uMie;
uniform vec3 uHazeColor;
uniform float uSurfaceDensity;
uniform float uCameraAltitude;
uniform float uCameraCloudMacro;
uniform float uCameraCloudDensity;
uniform float uPassMin;
uniform float uPassMax;
uniform float uPassCameraNear;
uniform float uPassCameraFar;
uniform mat4 uCloudProjection;
uniform vec3 uRingNormal;
uniform float uRingInnerRadius;
uniform float uRingOuterRadius;
uniform float uRingOpacity;

// --- Tunables ----------------------------------------------------------
// Keeps the solid-body occluder strictly below the camera. See
// 'solidOccluderDistance' for why that matters at all.
const float OCCLUDER_CAMERA_MARGIN = 1.0;

// Below this density (macro field) or accumulated alpha (per march step),
// the contribution is visually negligible; skip it rather than spend a
// sample or a fragment on something that will round to invisible anyway.
const float MACRO_DISCARD_THRESHOLD = 0.003;
const float STEP_DENSITY_THRESHOLD = 0.003;
const float ACCUM_ALPHA_DISCARD = 0.004;
const float ACCUM_ALPHA_SATURATED = 0.985; // early-out once the march is visually opaque
// Hardware depth represents the first materially visible part of the volume,
// not the mathematical shell entry. A tiny amount of extinction before this
// threshold is still included in the weighted fallback below, but cannot pull
// a distant cloud body all the way to the camera in the depth buffer.
const float CLOUD_DEPTH_ALPHA_THRESHOLD = 0.025;
// The local volume and the orbital sheet are two filters over the same global
// weather field. Blend by distance to the first cloud sample, never by camera
// altitude, so climbing through the layer cannot flip representations.
const float VOLUME_BLEND_START = 18000.0;
const float VOLUME_BLEND_END = 65000.0;
// A sparse short march can miss every dense lobe along one grazing ray. Keep a
// faint fraction of the global envelope while close, then restore the complete
// sheet through the handover; otherwise those misses line up into an annular
// clear-sky gap between the two representations.
const float VOLUME_ALPHA_FLOOR = 0.08;
// Widened from the original 0.22: with TOWER_TOP_MAX below, this now fully
// resolves to zero density with genuine headroom before the slab's outer
// bound instead of finishing 2% short of it, so a tall tower's own crown
// tapers to nothing instead of reading as a flat plane cut by the deck's
// outer radius.
const float VERTICAL_EDGE_SOFTNESS = 0.30;
// towerTop's upper clamp. Paired with VERTICAL_EDGE_SOFTNESS above: the top
// fade band (towerTop - softness, towerTop) must land entirely inside the
// slab, so this leaves a fixed clear margin at the outer radius regardless of
// how tall a given tower's macro/detail inputs would otherwise push it.
const float TOWER_TOP_MAX = 0.90;
const float VOLUME_EROSION = 0.95;
// The fine per-step field erodes a macro-cloud into separate bodies. Its floor
// stays just above zero for numerical continuity, but is deliberately low
// enough that erosion can open real air between nearby cumulus lobes.
const float DETAIL_DENSITY_MIN = 0.02;
const float DETAIL_DENSITY_MAX = 1.25;

// Converts a step's density*length into an alpha contribution. Tuned so a
// step through fully-dense cloud roughly this many metres thick reaches
// near-total opacity on its own.
const float STEP_ALPHA_LENGTH_SCALE = 650.0;

// Above the deck: fades the shell's own silhouette out toward grazing
// incidence (dot(deckUp, -viewDir) near 0) rather than leaving a hard edge
// at the true horizon. See the "Above the deck" branch below for why this is
// unconditional rather than gated to near-total-coverage worlds.
const float DECK_FADE_GRAZING_START = 0.0;
const float DECK_FADE_GRAZING_END = 0.12;

// Terminator softness: a deck is thick, so its tops stay lit after the
// ground beneath has gone dark. Range is dot(surfaceUp, sunDirection).
const float LIT_TERMINATOR_START = -0.22;
const float LIT_TERMINATOR_END = 0.30;

// Weak multiple scattering remains visible only in the twilight belt. Below
// this solar elevation the star is too far behind the planet for the upper
// atmosphere to illuminate the deck, so the cloud must become truly dark.
const float TWILIGHT_FILL_NIGHT_EDGE = -0.34;
const float TWILIGHT_FILL_DAY_EDGE = -0.08;

// Forward-scattering "silver lining" rim around the star as seen through
// the deck: pow() exponent shapes how tight the rim is, the multiplier caps
// its contribution.
const float SILVER_LINING_POWER = 8.0;
const float SILVER_LINING_STRENGTH = 0.7;

// A deck is optically thick, so brightness does not fall off as fast as a
// Lambertian surface across the terminator; this floors the shading term
// before the silver lining and the final "lit" multiply (which is what
// takes the night side fully to black).
const float SHADING_NIGHT_FLOOR = 0.12;

// uFogMultiplier-equivalent for the deck's own aerial perspective: unlike
// TerrainMaterial this is not exposed as a uniform (the deck has no per-body
// tuned value yet), just a flat gain matching the ballpark that was live
// there before it became a uniform.
const float DECK_FOG_GAIN = 1.6;
const float MEAN_ALTITUDE_FACTOR = 0.5; // averages fragment and camera altitude for the density sample

// Cloud opacity ramps from mostly-transparent to mostly-opaque across this
// range as the per-step detail field increases, so thin edges stay wispy
// while a dense core reads as solid.
const float ALPHA_DETAIL_BASE = 0.55;
const float ALPHA_DETAIL_WEIGHT = 0.55;

// A thin, backlit edge and an optically thick core scatter light back to the
// viewer differently even at the same fine-detail value: the core's own mass
// re-absorbs a fraction of what it scatters, a water cloud's actual
// self-shadowing. ALPHA_DETAIL_* above already varies shading with texture;
// this instead varies it with how much cloud the ray actually accumulated
// (accumAlpha), so a dense body reads visibly denser/darker than a wisp
// rather than the two differing only in opacity.
const float DENSITY_DARKEN_START = 0.45;
const float DENSITY_DARKEN_END = 0.95;
const float DENSITY_DARKEN_STRENGTH = 0.62;

/**
 * Both intersections of a ray with a sphere at the origin, or (-1, -1).
 *
 * The factored form of c avoids subtracting two numbers near 3.7e13 from each
 * other, which at planetary radii exhausts a 32-bit float's precision entirely.
 * See raySphere in common.glsl for the full explanation.
 */
vec2 raySphereBoth(vec3 origin, vec3 dir, float radius) {
  float b = dot(origin, dir);
  float len = length(origin);
  float c = (len - radius) * (len + radius);
  float d = b * b - c;
  if (d < 0.0) return vec2(-1.0, -1.0);
  float sd = sqrt(d);
  // The direct positive root (-b + sd) catastrophically cancels when the
  // camera is just inside a planetary shell and looks outward: both terms are
  // millions of metres while the cloud entry is only a few kilometres away.
  // Compute one well-conditioned root and recover the other from t0*t1=c.
  float q = -b - (b >= 0.0 ? sd : -sd);
  if (abs(q) < 1e-6) return vec2(-b - sd, -b + sd);
  float root0 = q;
  float root1 = c / q;
  return vec2(min(root0, root1), max(root0, root1));
}

/**
 * Per-pixel offset for the march's sample comb, as a fraction of one step.
 *
 * Interleaved gradient noise rather than a plain hash. Both break the coherent
 * banding a fixed-phase comb produces across a soft boundary, but a hash is
 * white noise: neighbouring pixels draw independently, so the residual error
 * clumps into the coarse, blotchy speckle reported where the deck meets a
 * mountain ridge. IGN is deliberately anisotropic and near-uniform in every
 * small neighbourhood, so the same amount of error is spread as a fine, even
 * grain instead — and being a closed-form function of the pixel it costs the
 * same as the hash it replaces. The other half of that fix is simply more
 * steps (see MARCH_STEPS_HIGH), which shrinks the error rather than
 * redistributing it.
 *
 * A single IGN tap's own anisotropy has a visible direction — up close, where
 * the march's error is coarse enough to see at all, it reads as faint
 * parallel streaks rather than grain. Averaging it with a second tap whose
 * x/y coefficients were swapped (so its own streaks ran the other way) was
 * meant to cancel that shared direction, but two linear ramps at different
 * angles don't cancel when summed — they interfere, the same way two sheets
 * of parallel lines overlaid at an angle produce a moire grid rather than a
 * flat tone. That swapped-coefficient tap traded one visible streak
 * direction for a visible diagonal cross-hatch, reported where a low deck
 * met a mountain ridge and, at low altitude, across the whole visible sheet
 * of fog. The second tap is now a hash with no linear relationship to the
 * pixel coordinate at all, so it has nothing for the IGN tap's ramp to beat
 * against; the sum keeps the near-uniform coverage the IGN tap alone
 * provides (this is still not two independent hashes, which is the blotchy
 * speckle the IGN tap was chosen over in the first place) while landing
 * closer to a triangular distribution than a flat one, the theoretically
 * preferred shape for dither noise generally (TPDF), not just a side effect
 * here.
 */
float sampleDither(vec2 pixel) {
  float a = fract(52.9829189 * fract(0.06711056 * pixel.x + 0.00583715 * pixel.y));
  float b = hash13(vec3(pixel, 71.238));
  return (a + b) * 0.5;
}

float nearestPositive(vec2 roots) {
  if (roots.x > 0.0) return roots.x;
  if (roots.y > 0.0) return roots.y;
  return -1.0;
}

/**
 * Per-FRAGMENT (not per-step) lobe-size scale for the near-field march's
 * erosion noise. Used to be a ~42 km fbm field continuously remapped to
 * 0.6..1.8 and multiplied into cloudErosionAt's own frequency, so nearby
 * cumulus clusters would read as different characteristic sizes.
 *
 * That continuous frequency modulation turned out to be the source of a
 * swirling, combed "brushed metal" look reported on Meridia's near-total
 * overcast — confirmed on a real GPU by hard-coding this function to a flat
 * 1.0 and watching the swirl disappear completely, then reproducing it again
 * with the range narrowed to 0.88..1.16, which still swirled. Continuously
 * varying a noise field's own frequency across screen space is a classic
 * phase-distortion pattern generator regardless of how narrow the range is,
 * so there is no tuning fix here — only removing the modulation, which is
 * what this now does. Kept as a function taking the same parameters, unused
 * or not, so a future non-swirling variety mechanism (e.g. a discretised
 * lobe field with hard region boundaries, rather than a continuous one) has
 * one place to live without touching every call site again.
 */
float cloudLobeScaleAt(vec3 samplePos, float time, float drift, float planetRadius) {
  return 1.0;
}

/**
 * Per-step erosion for the near-field march: how far a step's density is
 * pushed above or below the macro/fine base, before the vertical taper.
 * Returns a signed term already centred on zero (so callers just add
 * 'result * VOLUME_EROSION'), and folds in three things a single fixed-scale
 * fbm cannot give:
 *
 *  - LOBE SIZE. 'lobeScale' (see cloudLobeScaleAt) remaps this noise's own
 *    frequency per region, so nearby cumulus clusters read as different
 *    characteristic sizes instead of every lobe in the sky being built from
 *    the same 1150 m cell.
 *  - MICRO DETAIL. A small, fixed ~260 m single-octave fbm roughs up what
 *    would otherwise be a smooth erosion gradient, breaking up the
 *    airbrushed look on near approach.
 *  - CLUSTERING. Erosion's ability to CREATE density (erosion above its
 *    midpoint) is scaled by the macro field, so a sparse macro region stays
 *    genuinely clear between clusters instead of erosion painting a uniform
 *    speckle of isolated puffs across the whole sky. Erosion can still freely
 *    carve (push density down) inside a macro-favoured region, which is what
 *    breaks one macro blob into several separated bodies rather than a solid
 *    slab.
 */
float cloudErosionAt(
  vec3 samplePos,
  float time,
  float drift,
  float planetRadius,
  float marchMacro,
  float lobeScale
) {
  float weatherTravel = time * drift * planetRadius;
  vec3 wind = vec3(weatherTravel, 0.0, weatherTravel * 0.6);
  float erosion = fbm((samplePos + wind) * (1.0 / (1150.0 * lobeScale)), 2);
  float micro = fbm((samplePos + wind) * (1.0 / 260.0), 1);
  float combined = mix(erosion, micro, 0.28) - 0.5;
  return combined > 0.0 ? combined * marchMacro : combined;
}

/**
 * Per-FRAGMENT (not per-step) variation for the "how much cloud mass is
 * here" baseline while the camera is immersed inside the deck
 * (uCameraCloudMacro, sampled once per frame at the observer and otherwise
 * shared by every ray direction and every march step for the reason given at
 * cameraInsideDeck's declaration: the ANGULAR macro field has nowhere near
 * enough resolution over a few kilometres of travel to vary on its own — see
 * the file comment on why cloudDetailAt/cloudErosionAt exist as METRIC
 * fields instead).
 *
 * That fixed the too-coarse problem but created a flatter one: with the
 * baseline pinned to one number for the whole visible frame, only the fine
 * texture on top of it varied, and fine texture alone lacks the contrast to
 * carve a genuinely clear pocket out of a locked-in-dense baseline — the
 * frame reads as uniform haze regardless of how much fine structure it
 * carries. This adds a ~4.2 km METRIC lobe evaluated once per fragment (at
 * the march's entry point, not every step — a first version resampled it up
 * to 14x per fragment, which was the single largest cost this pass added)
 * so the baseline still varies between one ray and its neighbour, making "am
 * I near the edge of this mass" a real, direction-dependent question again
 * instead of a frame-wide constant. It no longer varies along one ray's own
 * length, which matters only for a near-horizontal ray immersed in a thin
 * shell (see VOLUME_BLEND_END) — the per-step erosion/detail terms above
 * still vary there regardless.
 */
float cloudMacroVariationAt(vec3 samplePos, float time, float drift, float planetRadius) {
  float weatherTravel = time * drift * planetRadius;
  vec3 wind = vec3(weatherTravel, 0.0, weatherTravel * 0.6);
  return fbm((samplePos + wind) * (1.0 / 4200.0), 2) - 0.5;
}

/**
 * Distance at which this ray is swallowed by the solid body, or -1 when it
 * never is.
 *
 * Two things separate this from a plain 'nearestPositive' against the datum
 * sphere, and both were live bugs.
 *
 * ENTRY, NOT EXIT. Only the *near* root is an occlusion; the far root is where
 * the ray leaves again. They differ exactly when the camera sits inside the
 * sphere, which on Halcyon is the normal state of affairs near the water:
 * 'seaLevelFraction' is negative there, so the sea surface — and every metre
 * of air for hundreds of metres above it — is below the datum radius. Taking
 * the exit root there reported an occluder a few hundred metres ahead in every
 * direction including straight up, so every cloud fragment on the planet was
 * discarded. That is the "clouds disappear below about 600 m" report, and the
 * altitude it happens at is exactly -seaLevel.
 *
 * THE WATERLINE, NOT THE DATUM. On an ocean world with a *positive* sea level
 * (Thalassa) the datum sphere sits kilometres beneath the water, so its horizon
 * is much further away than the real one — leaving an annulus past the true
 * horizon where the deck on the planet's far side was never occluded by
 * anything and composited straight over the ground. Occluding at the waterline
 * closes it.
 *
 * The radius is finally held just under the camera, so a viewer who is below
 * the nominal occluder (Halcyon again) still gets a horizon — its own — rather
 * than no occlusion at all, which would reopen the far-side leak from the other
 * side.
 */
float solidOccluderDistance(vec3 rayOrigin, vec3 rayDir, float camR) {
  float radius = min(uOccluderRadius, camR - OCCLUDER_CAMERA_MARGIN);
  if (radius <= 0.0) return -1.0;
  vec2 roots = raySphereBoth(rayOrigin, rayDir, radius);
  return roots.x > 0.0 ? roots.x : -1.0;
}

void main() {
  // The deck is a closed shell around the planet, so from anywhere inside it
  // the far half projects across the whole lower screen. Without this the
  // underside of the sky is painted over the ground: the far half is thousands
  // of kilometres away, lands in the far depth partition, and there is no
  // terrain that far out to occlude it below the horizon.
  // The raw proxy positions interpolate to the exact point where this view ray
  // crosses a proxy triangle. Only use the triangle to launch analytic sphere
  // intersections; its tessellation must never shape the weather itself.
  vec3 rayDir = normalize(vRenderPosition - cameraPosition);
  vec3 rayDirFixed = normalize(vLocal - uCameraFixed);
  vec3 rayOrigin = cameraPosition - uPlanetCentre;
  float fragDistance = nearestPositive(raySphereBoth(rayOrigin, rayDir, uProxyRadius));
  if (fragDistance <= 0.0) discard;
  float camR = length(rayOrigin);
  float planetHit = solidOccluderDistance(rayOrigin, rayDir, camR);

  // Locate that analytic weather point in the planet-fixed frame. Rotation
  // preserves distance along the ray, so density remains continuous across
  // proxy triangles.
  float weatherDistance = nearestPositive(raySphereBoth(rayOrigin, rayDir, uDeckRadius));
  if (weatherDistance <= 0.0) discard;
  // That analytic point is where the ray meets the OUTER shell, and for any ray
  // at or below the horizon the nearest such crossing is on the planet's FAR
  // side — the ray reaches it only by passing through the body. Sampling the
  // weather field there gave the fragment a far-hemisphere density, which is
  // the reported "clouds from the other side of the planet, seen through it":
  // a stippled band of the wrong continent's weather hugging the horizon.
  //
  // The march guards below were never enough on their own, because this sample
  // decides the fragment's macro density (and its sheet blend) before any march
  // happens.
  //
  // CLAMPED, not discarded. Dropping the fragment outright also threw away the
  // near cloud the march would have drawn along the first part of the same ray,
  // which emptied the sky of everything while flying inside the deck. The
  // sample is pulled back to the last point the ray is actually visible at
  // instead, so the field is read in front of the planet and the near volume
  // survives.
  if (planetHit > 0.0) weatherDistance = min(weatherDistance, planetHit);
  vec3 local = normalize(uCameraFixed + rayDirFixed * weatherDistance);

  // The macro shape, shared with the terrain's shadow lookup — see the file
  // comment for why it is sampled once here rather than per march step.
  float macroDetail;
  float macro = cloudDensityAt(local, uTime, uDrift, uCoverage, uBanded, macroDetail);
  if (macro <= MACRO_DISCARD_THRESHOLD && uCameraCloudMacro <= MACRO_DISCARD_THRESHOLD) discard;

  // Slab bounds are computed once on the CPU (see the constructor) rather than
  // re-derived here, so the height-normalising thickness always matches the
  // inner sphere it is measured from.
  float innerRadius = uInnerRadius;
  // The orbital sheet samples the global map at the remote shell hit. That is
  // correct outside the deck, but from inside it is a static texture projected
  // around the observer. Inside the slab, always hand ownership to the weather
  // mass at the camera. Metric samples along each ray then provide the only
  // visible structure, including inside fine-scale clear pockets.
  bool cameraInsideDeck = camR >= innerRadius && camR <= uDeckRadius;
  float marchMacro = cameraInsideDeck ? uCameraCloudMacro : macro;

  float accumAlpha;
  float detail;
  // The proxy launches the ray, but it is not where the visible cloud lives.
  // This distance is replaced with the actual slab entry in every branch and
  // later drives both pass ownership and the hardware depth test.
  float cloudSurfaceDistance = fragDistance;
  float cloudVisibleDistance = fragDistance;

  if (camR < innerRadius) {
    // Below the deck: the common case of standing on the ground, and the one
    // this march exists for — see the file comment. The camera is inside the
    // hollow, so it is guaranteed to cross into the slab material once.
    //
    // Render- and fixed-space directions came from the same
    // perspective-correct proxy point, so metric samples stay continuous.
    vec3 marchDir = rayDir;
    float tEnd = nearestPositive(raySphereBoth(rayOrigin, marchDir, uDeckRadius));
    float tStart = nearestPositive(raySphereBoth(rayOrigin, marchDir, innerRadius));
    // A grazing ray can miss the inner sphere by a hair despite the camera
    // being inside it, from the same kind of precision loss; treat that as
    // "the whole segment is slab" rather than discarding good fragments.
    if (tStart <= 0.0 || tStart >= tEnd) tStart = 0.0;
    if (planetHit > 0.0 && planetHit < tStart) discard;
    // The solid body ends the ray. Discarding only when the planet is nearer
    // than tStart guards the case where the whole segment is behind it and
    // leaves the one where it sits in the MIDDLE: the march then walked
    // straight through the planet and accumulated the deck on its far side
    // into both the colour and the depth estimate — clouds visible through the
    // world, which is what this looked like from low altitude over an island.
    if (planetHit > 0.0) tEnd = min(tEnd, planetHit);
    if (tEnd <= tStart) discard;
    cloudSurfaceDistance = max(tStart, 0.0);

    int steps = int(uMarchSteps);
    // A grazing ray can remain inside a planetary shell for hundreds of
    // kilometres. Marching all of it turns even 0.3% density opaque and makes
    // the cloud-map threshold a hard horizontal wall. Nearby depth is enough;
    // the shared sheet represents the rest of the ray continuously.
    float marchEnd = min(tEnd, tStart + VOLUME_BLEND_END * 1.25);
    float stepLen = (marchEnd - tStart) / float(steps);
    // Dithers the whole sample comb by a per-fragment fraction of one step.
    // A grazing ray crosses a soft macro/vertical boundary over many step
    // lengths of real distance, but only ~10 fixed-phase samples resolve it;
    // every fragment along the boundary lands on the same side of the same
    // step, aliasing the smooth 3D transition into a hard, coherent band —
    // reported as a straight edge where cloud coverage changes, worst at
    // shallow viewing angles just above the deck. Offsetting the comb
    // independently per pixel scatters that band into per-pixel noise
    // instead, which reads as the soft, grainy transition a real edge this
    // thin should have. See 'sampleDither' for why the offset is a
    // low-discrepancy sequence rather than a hash: the same trade, but with
    // the residual error spread evenly instead of clumping into speckle.
    float jitter = sampleDither(gl_FragCoord.xy);
    // Once per fragment, not once per step — see cloudLobeScaleAt.
    float lobeScale = cloudLobeScaleAt(uCameraFixed + rayDirFixed * tStart, uTime, uDrift, uPlanetRadius);

    accumAlpha = 0.0;
    float detailAccum = 0.0;
    float weightAccum = 0.0;
    float depthWeightAccum = 0.0;
    float depthDistanceAccum = 0.0;
    float firstVisibleDistance = -1.0;

    for (int i = 0; i < ${MAX_MARCH_STEPS}; i++) {
      if (i >= steps) break;
      float t = tStart + (float(i) + jitter) * stepLen;
      vec3 samplePos = uCameraFixed + rayDirFixed * t;
      float fine = cloudDetailAt(samplePos, uTime, uDrift, uPlanetRadius);
      float height01 = clamp((length(samplePos) - innerRadius) / max(uDeckThickness, 1.0), 0.0, 1.0);
      float towerTop = clamp(0.50 + marchMacro * 0.46 + (fine - 0.5) * 0.30, 0.30, TOWER_TOP_MAX);
      float vertical = smoothstep(0.0, VERTICAL_EDGE_SOFTNESS, height01)
        * (1.0 - smoothstep(towerTop - VERTICAL_EDGE_SOFTNESS, towerTop, height01));
      float erosionTerm = cloudErosionAt(samplePos, uTime, uDrift, uPlanetRadius, marchMacro, lobeScale);
      float density = clamp(
        marchMacro * mix(DETAIL_DENSITY_MIN, DETAIL_DENSITY_MAX, fine)
          + erosionTerm * VOLUME_EROSION,
        0.0,
        1.0
      ) * vertical;
      if (density <= STEP_DENSITY_THRESHOLD) continue;
      float stepAlpha = clamp(density * stepLen / STEP_ALPHA_LENGTH_SCALE, 0.0, 1.0);
      float w = (1.0 - accumAlpha) * stepAlpha;
      accumAlpha += w;
      detailAccum += fine * w;
      weightAccum += w;
      depthDistanceAccum += t * w;
      depthWeightAccum += w;
      if (firstVisibleDistance < 0.0 && accumAlpha >= CLOUD_DEPTH_ALPHA_THRESHOLD) {
        firstVisibleDistance = t;
      }
      if (accumAlpha > ACCUM_ALPHA_SATURATED) break;
    }
    detail = weightAccum > 0.0 ? detailAccum / weightAccum : macroDetail;
    float sheetBlend = smoothstep(VOLUME_BLEND_START, VOLUME_BLEND_END, tStart);
    float volumeAlpha = max(accumAlpha, marchMacro * VOLUME_ALPHA_FLOOR);
    accumAlpha = mix(volumeAlpha, macro, sheetBlend);
    detail = mix(detail, macroDetail, sheetBlend);
    // Falls back to the far end of the march, not the camera, when nothing
    // crossed the visibility threshold: this fragment is only visible at all
    // because of the ambient VOLUME_ALPHA_FLOOR haze spread across the whole
    // marched segment, not a real detected body, and placing it at the
    // camera made the planet-occlusion check below (and the real depth test
    // against rendered terrain) never fire for it — a distant mountain, or
    // the planet's own far hemisphere, could no longer hide it. See the
    // "close to or inside the layer" branch for the report this fixed.
    float sampledVisibleDistance = firstVisibleDistance >= 0.0
      ? firstVisibleDistance
      : (depthWeightAccum > 0.0 ? depthDistanceAccum / depthWeightAccum : marchEnd);
    cloudVisibleDistance = mix(sampledVisibleDistance, tStart, sheetBlend);
  } else if (camR < uDeckRadius + VOLUME_BLEND_END) {
    // Close to or inside the layer, march the actual spherical slab. This is
    // the path that turns a nearby texture sheet into cloud bodies with a soft
    // base and top. From outside, only the near half of the shell is marched;
    // from inside, the nearest boundary ends the segment.
    vec2 outerRoots = raySphereBoth(rayOrigin, rayDir, uDeckRadius);
    vec2 innerRoots = raySphereBoth(rayOrigin, rayDir, innerRadius);
    float tStart = 0.0;
    float tEnd = -1.0;

    if (camR > uDeckRadius) {
      tStart = outerRoots.x;
      if (tStart > 0.0) {
        tEnd = innerRoots.x > tStart ? innerRoots.x : outerRoots.y;
      }
    } else {
      float outerExit = nearestPositive(outerRoots);
      float innerExit = nearestPositive(innerRoots);
      tEnd = innerExit > 0.0 ? min(outerExit, innerExit) : outerExit;
    }
    if (tStart < 0.0 || tEnd <= tStart) discard;
    if (planetHit > 0.0 && planetHit < tStart) discard;
    // Same clamp as the below-deck branch above, for the same reason.
    if (planetHit > 0.0) tEnd = min(tEnd, planetHit);
    if (tEnd <= tStart) discard;
    cloudSurfaceDistance = max(tStart, 0.0);

    int steps = int(uMarchSteps);
    float marchEnd = min(tEnd, tStart + VOLUME_BLEND_END * 1.25);
    float stepLen = (marchEnd - tStart) / float(steps);
    // See the "below the deck" branch above for why this is dithered rather
    // than sampled at a fixed 0.5-step phase.
    float jitter = sampleDither(gl_FragCoord.xy);
    // Both once per fragment, not once per step — see cloudLobeScaleAt and
    // cloudMacroVariationAt. Below the deck, marchMacro is already the
    // per-ray cloudDensityAt sample and varies naturally; immersed inside
    // it, marchMacro is the single uCameraCloudMacro scalar (see
    // cloudMacroVariationAt's comment), so give it real per-fragment spread
    // here so the deck's own interior isn't a frame-wide constant.
    vec3 marchEntryPos = uCameraFixed + rayDirFixed * tStart;
    float lobeScale = cloudLobeScaleAt(marchEntryPos, uTime, uDrift, uPlanetRadius);
    float macroLocal = cameraInsideDeck
      ? clamp(marchMacro + cloudMacroVariationAt(marchEntryPos, uTime, uDrift, uPlanetRadius) * 1.1, 0.0, 1.0)
      : marchMacro;
    accumAlpha = 0.0;
    float detailAccum = 0.0;
    float weightAccum = 0.0;
    float depthWeightAccum = 0.0;
    float depthDistanceAccum = 0.0;
    float firstVisibleDistance = -1.0;

    for (int i = 0; i < ${MAX_MARCH_STEPS}; i++) {
      if (i >= steps) break;
      float t = tStart + (float(i) + jitter) * stepLen;
      vec3 samplePos = uCameraFixed + rayDirFixed * t;
      float height01 = clamp((length(samplePos) - innerRadius) / max(uDeckThickness, 1.0), 0.0, 1.0);
      float fine = cloudDetailAt(samplePos, uTime, uDrift, uPlanetRadius);
      float towerTop = clamp(0.50 + macroLocal * 0.46 + (fine - 0.5) * 0.30, 0.30, TOWER_TOP_MAX);
      float vertical = smoothstep(0.0, VERTICAL_EDGE_SOFTNESS, height01)
        * (1.0 - smoothstep(towerTop - VERTICAL_EDGE_SOFTNESS, towerTop, height01));
      float erosionTerm = cloudErosionAt(samplePos, uTime, uDrift, uPlanetRadius, macroLocal, lobeScale);
      float density = clamp(
        macroLocal * mix(DETAIL_DENSITY_MIN, DETAIL_DENSITY_MAX, fine)
          + erosionTerm * VOLUME_EROSION,
        0.0,
        1.0
      ) * vertical;
      if (cameraInsideDeck) {
        // Anchor the first few dozen metres to the density actually measured
        // at the observer — genuinely isotropic at arm's reach, since a real
        // droplet field looks the same in every direction that close. The
        // decay length used to be 1200m, which kept this single non-varying
        // scalar dominant across most of a typical march budget and washed
        // out every per-step spatial detail term (erosion, multi-scale lobes)
        // added above: nearly every visible pixel blended mostly toward the
        // same flat number, reading as uniform haze regardless of how much
        // structure the noise fields actually had. Shrunk so real,
        // direction-dependent structure takes over within a couple hundred
        // metres instead of a couple kilometres.
        float localAnchor = exp(-t / 220.0) * 0.65;
        density = mix(density, uCameraCloudDensity, localAnchor);
      }
      if (density <= STEP_DENSITY_THRESHOLD) continue;
      float stepAlpha = 1.0 - exp(-density * stepLen / STEP_ALPHA_LENGTH_SCALE);
      float w = (1.0 - accumAlpha) * stepAlpha;
      accumAlpha += w;
      detailAccum += (fine * 0.7 + height01 * 0.3) * w;
      weightAccum += w;
      depthDistanceAccum += t * w;
      depthWeightAccum += w;
      if (firstVisibleDistance < 0.0 && accumAlpha >= CLOUD_DEPTH_ALPHA_THRESHOLD) {
        firstVisibleDistance = t;
      }
      if (accumAlpha > ACCUM_ALPHA_SATURATED) break;
    }
    detail = weightAccum > 0.0 ? detailAccum / weightAccum : macroDetail;
    float sheetBlend = smoothstep(
      VOLUME_BLEND_START,
      VOLUME_BLEND_END,
      max(0.0, tStart)
    );
    float volumeAlpha = max(accumAlpha, marchMacro * VOLUME_ALPHA_FLOOR);
    accumAlpha = mix(volumeAlpha, macro, sheetBlend);
    detail = mix(detail, macroDetail, sheetBlend);
    // Falls back to the far end of the march (not the camera/tStart) when
    // nothing crossed the visibility threshold along this specific ray.
    //
    // This used to fall back to tStart (~0, "the cloud is right at the
    // camera") whenever the AMBIENT uCameraCloudDensity scalar sampled once
    // at the observer's own position was above 0.05, regardless of what this
    // ray actually found — "Dense local fog genuinely begins at the
    // observer" was the intent, but uCameraCloudDensity carries no
    // direction, so it also fired for rays through a genuinely clear gap
    // while merely standing in a moderately hazy pocket. Per-step density
    // already blends toward uCameraCloudDensity near t=0 (the localAnchor
    // term above), which alone pulls firstVisibleDistance close to the
    // camera in genuinely thick fog — so the explicit override was not just
    // unnecessary but actively wrong for a ray that finds real, clear air:
    // pinning its depth to the camera made the planet-occlusion discard
    // below (and the real depth test against rendered terrain) never fire,
    // so a cloud body on the planet's far hemisphere — which should be
    // hidden behind the planet's own bulk — stayed visible through it, and a
    // nearby mountain could no longer occlude the deck behind it either.
    // Reported as "clouds from the other side of the planet visible while
    // inside the deck".
    float sampledVisibleDistance = firstVisibleDistance >= 0.0
      ? firstVisibleDistance
      : (depthWeightAccum > 0.0 ? depthDistanceAccum / depthWeightAccum : marchEnd);
    cloudVisibleDistance = mix(sampledVisibleDistance, tStart, sheetBlend);
  } else {
    float outerEntry = nearestPositive(raySphereBoth(rayOrigin, rayDir, uDeckRadius));
    if (outerEntry <= 0.0) discard;
    cloudSurfaceDistance = outerEntry;
    cloudVisibleDistance = outerEntry;
    if (planetHit > 0.0 && planetHit < cloudSurfaceDistance) discard;
    // Above the deck, or flying through it: orbital and high-altitude views,
    // where the flat-wash problem the march exists for does not apply — the
    // macro field alone already reads as clouds from far enough away to see
    // more than one of its cells. Unchanged from before the march existed.
    accumAlpha = macro;
    detail = macroDetail;

    // Faded by the grazing angle at this exact fragment (how close to tangent
    // the view ray is here), not by how far this point sits from the planet's
    // core — the deck's own visible rim is bounded by its own (small) radius
    // regardless, so a core-distance measure never reaches a meaningfully
    // faded range before the fragment stops being visible at all.
    //
    // Applied unconditionally, not only for near-total-coverage worlds this
    // used to be gated to: 'macro' has no notion of the ray's actual path
    // length through the shell, so without this a normal-coverage deck's own
    // silhouette edge is a hard, unfaded boundary against whatever is behind
    // it right at the true horizon — reported back as a hard bright line
    // there on Halcyon (52% coverage), independent of the near-total-coverage
    // case this was originally written for.
    vec3 deckUp = normalize(rayOrigin + rayDir * cloudSurfaceDistance);
    float grazing = dot(deckUp, -rayDir);
    float deckFade = smoothstep(DECK_FADE_GRAZING_START, DECK_FADE_GRAZING_END, grazing);
    accumAlpha *= deckFade;
  }
  if (accumAlpha <= ACCUM_ALPHA_DISCARD) discard;

  // The ray march may start inside the nominal cloud shell, but the first
  // actual cloud body can still be kilometres away.  Test that visible point
  // against the solid planet after marching: using the mathematical shell
  // entry (often zero here) lets clouds from the far hemisphere survive and
  // composite over the ground.  Local fog remains valid because its visible
  // distance is genuinely before the surface hit.
  if (planetHit > 0.0 && planetHit < cloudVisibleDistance) discard;

  // Transparent clouds are owned and depth-tested at the first optically
  // visible point in the slab. All partition limits are view-axis depths, not Euclidean lengths
  // along the ray: mixing those units at oblique angles collapsed fragments
  // onto a near/far plane and drew the old horizontal triangular wedges.
  // The vertex shader makes the raster proxy available in every pass, so this
  // real depth can now select exactly one owner without creating a clip gap.
  vec3 cloudOwnerRender = cameraPosition + rayDir * cloudVisibleDistance;
  float cloudRasterDepth = -(viewMatrix * vec4(cloudOwnerRender, 1.0)).z;
  if (cloudRasterDepth < uPassMin || cloudRasterDepth >= uPassMax) discard;

  // When the camera is inside dense cloud, the first visible sample is the eye
  // itself. Move only that case to the owning camera's near plane. Convert the
  // Z-plane distance back to distance along this ray before projecting it.
  float rayForward = max(1e-4, -(viewMatrix * vec4(rayDir, 0.0)).z);
  float depthDistance = clamp(
    cloudVisibleDistance,
    uPassCameraNear * 1.01 / rayForward,
    uPassCameraFar * 0.999 / rayForward
  );
  vec3 cloudSurfaceRender = cameraPosition + rayDir * depthDistance;
  vec4 cloudDepthClip = uCloudProjection * viewMatrix * vec4(cloudSurfaceRender, 1.0);
  float cloudDepthNdc = cloudDepthClip.z / cloudDepthClip.w;
  if (cloudDepthNdc < -1.0 || cloudDepthNdc > 1.0) discard;
  gl_FragDepth = cloudDepthNdc * 0.5 + 0.5;

  // Lighting.
  //
  // Cloud emits nothing. Every term here is a fraction of the sunlight falling
  // on this piece of deck, so a cloud on the night side goes black exactly like
  // the ground beneath it. The previous constant floor meant an unlit cloud
  // still returned a third of its albedo, which is self-illumination however it
  // is labelled — visible as a deck glowing over a dark planet.
  vec3 cloudPoint = cameraPosition + rayDir * cloudVisibleDistance;
  vec3 up = normalize(cloudPoint - uPlanetCentre);
  vec3 L = normalize(uSunDirection);
  float sunUp = dot(up, L);

  // The terminator is soft because a deck is thick: its tops are still lit
  // after the ground below has gone dark.
  float lit = smoothstep(LIT_TERMINATOR_START, LIT_TERMINATOR_END, sunUp);

  // Forward scattering: cloud is optically thick but strongly forward
  // scattering, so a deck between the viewer and the star is rimmed with light.
  vec3 view = rayDir;
  float forward = max(dot(view, L), 0.0);
  float silver = pow(forward, SILVER_LINING_POWER) * SILVER_LINING_STRENGTH;

  // A deck is optically thick, so its brightness does not fall off as fast as a
  // Lambertian surface across the terminator; this shapes that ramp. It is a
  // fraction of the incident light throughout, and the final multiply by "lit"
  // is what takes the night side to black.
  float shading = mix(SHADING_NIGHT_FLOOR, 1.0, lit) + silver * lit;

  float ringShadow = ringShadowAtPoint(
    cloudPoint - uPlanetCentre,
    L,
    uRingNormal,
    uRingInnerRadius,
    uRingOuterRadius,
    uRingOpacity
  );
  vec3 directCloud = uCloudColor * uSunColor * uSunIntensity
    * shading * lit * (1.0 - ringShadow);
  // Multiple-scattered sky light keeps clouds legible around dawn and dusk,
  // but it cannot illuminate the deep night hemisphere. Gate it by solar
  // elevation before removing it again as direct daylight takes ownership.
  float ambientCloud = 0.025 + 0.035 * clamp(uSurfaceDensity, 0.0, 2.0);
  vec3 nightFill = mix(vec3(0.012), uHazeColor, 0.38) * ambientCloud * min(uSunIntensity, 1.0);
  float twilightFill = smoothstep(TWILIGHT_FILL_NIGHT_EDGE, TWILIGHT_FILL_DAY_EDGE, sunUp)
    * (1.0 - lit);
  vec3 colour = directCloud + nightFill * twilightFill;
  // The volume has no expensive secondary-light march, but the density-weighted
  // detail still gives large bodies a readable shaded core instead of a flat
  // alpha texture.
  colour *= mix(0.58, 1.20, smoothstep(0.12, 0.88, clamp(detail, 0.0, 1.0)));
  colour *= mix(
    1.0,
    DENSITY_DARKEN_STRENGTH,
    smoothstep(DENSITY_DARKEN_START, DENSITY_DARKEN_END, clamp(accumAlpha, 0.0, 1.0))
  );

  // --- Aerial perspective --------------------------------------------------
  // Same treatment TerrainMaterial gives the ground: without this the deck
  // reads as floating in vacuum, exactly as crisp and undimmed at the horizon
  // or from orbit as it is close up, while the ground beneath it visibly hazes
  // with distance. The air column between camera and deck is the same air the
  // ground's aerial perspective already accounts for.
  if (uSurfaceDensity > 0.0) {
    float fragAltitude = length(cloudPoint - uPlanetCentre) - uPlanetRadius;
    // A grazing ray from high camera altitude still dips down to dense, low
    // atmosphere near its midpoint even though both endpoints (camera, cloud
    // fragment) are high up. Averaging just the two endpoint altitudes misses
    // that dip entirely and collapses density on exactly those rays, which is
    // what let a distant cloud's raw (near-white, for Meridia) albedo show
    // through undimmed and unhazed instead of taking on the same tint the
    // same clouds show up close. Fold in the altitude at the ray's closest
    // approach to the planet's centre (clamped to the visible segment) and
    // take the lower of the two altitude estimates, so density is never
    // underestimated relative to the plain endpoint-average case.
    float closestApproachT = clamp(-dot(rayOrigin, rayDir), 0.0, cloudVisibleDistance);
    float closestApproachAltitude = length(rayOrigin + rayDir * closestApproachT) - uPlanetRadius;
    float meanAltitude = max(0.0, min(
      closestApproachAltitude,
      (fragAltitude + uCameraAltitude) * MEAN_ALTITUDE_FACTOR
    ));
    float density = uSurfaceDensity * exp(-meanAltitude / max(uScaleHeight, AERIAL_SCALE_HEIGHT_FLOOR));
    float opticalDepth = cloudVisibleDistance * density;

    vec3 extinction = exp(-(uRayleigh + vec3(uMie)) * opticalDepth * DECK_FOG_GAIN);
    float sunUpAmt = clamp(sunUp * 0.5 + 0.5, 0.0, 1.0);
    float sunlitAir = smoothstep(AERIAL_SUNLIT_AIR_START, AERIAL_SUNLIT_AIR_END, sunUp);
    vec3 inscatter = uHazeColor * uSunColor * uSunIntensity * (1.0 - extinction)
      * (AERIAL_MULTI_SCATTER_FLOOR + sunUpAmt * AERIAL_INSCATTER_SUNUP_WEIGHT) * sunlitAir;
    // Low sun: same twilight reddening + dimming TerrainMaterial applies to the
    // ground fog — the closed-form sun-beam optical depth, relative to zenith,
    // so the deck hazing toward the horizon warms up with the sky it floats
    // against instead of staying in the daytime haze hue. Density is clamped
    // like the shell's uDensityScale so thick worlds can't zero the deck out.
    float beamDensityScale = clamp(uSurfaceDensity / AERIAL_DENSITY_SCALE_NOMINAL,
                                   AERIAL_DENSITY_SCALE_MIN, AERIAL_DENSITY_SCALE_MAX);
    float sunAirmass = 1.0 / max(sunUp, AERIAL_SUN_BEAM_SIN_FLOOR);
    vec3 beamTau = (uRayleigh + vec3(uMie) * AERIAL_MIE_OPTICAL_DEPTH_GAIN)
                 * beamDensityScale * uScaleHeight * (sunAirmass - 1.0);
    inscatter *= exp(-beamTau);

    colour = colour * extinction + inscatter;
  }

  // Thicker cloud is more opaque; the edges stay wispy.
  float alpha = clamp(accumAlpha * (ALPHA_DETAIL_BASE + detail * ALPHA_DETAIL_WEIGHT), 0.0, 1.0);

  // See AtmosphereMaterial: one dither threshold across every layer, so the
  // impostor's own clouds are not doubled with the real deck's mid-fade.
  if (ditheredOut(uLayerFade, gl_FragCoord.xy)) discard;
  gl_FragColor = vec4(colour, alpha);

  // See TerrainMaterial.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface CloudUniformSet {
  uPlanetCentre: THREE.IUniform<THREE.Vector3>;
  uCameraFixed: THREE.IUniform<THREE.Vector3>;
  uPlanetRadius: THREE.IUniform<number>;
  uOccluderRadius: THREE.IUniform<number>;
  uInnerRadius: THREE.IUniform<number>;
  uProxyRadius: THREE.IUniform<number>;
  uSunDirection: THREE.IUniform<THREE.Vector3>;
  uSunColor: THREE.IUniform<THREE.Color>;
  uSunIntensity: THREE.IUniform<number>;
  uCloudColor: THREE.IUniform<THREE.Color>;
  uCoverage: THREE.IUniform<number>;
  uTime: THREE.IUniform<number>;
  uDrift: THREE.IUniform<number>;
  uBanded: THREE.IUniform<number>;
  /** Cross-fade with the impostor: 1 present, 0 absent. */
  uLayerFade: THREE.IUniform<number>;
  uDeckRadius: THREE.IUniform<number>;
  uDeckThickness: THREE.IUniform<number>;
  uMarchSteps: THREE.IUniform<number>;
  uScaleHeight: THREE.IUniform<number>;
  uRayleigh: THREE.IUniform<THREE.Vector3>;
  uMie: THREE.IUniform<number>;
  uHazeColor: THREE.IUniform<THREE.Color>;
  uSurfaceDensity: THREE.IUniform<number>;
  uCameraAltitude: THREE.IUniform<number>;
  uCameraCloudMacro: THREE.IUniform<number>;
  uCameraCloudDensity: THREE.IUniform<number>;
  uPassMin: THREE.IUniform<number>;
  uPassMax: THREE.IUniform<number>;
  uPassCameraNear: THREE.IUniform<number>;
  uPassCameraFar: THREE.IUniform<number>;
  uCloudProjection: THREE.IUniform<THREE.Matrix4>;
  uRingNormal: THREE.IUniform<THREE.Vector3>;
  uRingInnerRadius: THREE.IUniform<number>;
  uRingOuterRadius: THREE.IUniform<number>;
  uRingOpacity: THREE.IUniform<number>;
}

export class CloudLayer {
  readonly root = new THREE.Group();
  private readonly mesh: THREE.Mesh;
  private readonly interiorMesh: THREE.Mesh | null;
  private readonly ringSystem: PlanetRingSystem | null;
  private readonly uniforms: CloudUniformSet;
  private readonly proxyRadius: number;
  private insideProxy = true;

  /** Current local volumetric density, exposed for deterministic GPU captures. */
  get cameraCloudDensity(): number {
    return this.uniforms.uCameraCloudDensity.value;
  }

  /**
   * Everything the terrain shader needs to reproduce this deck's shadow, in one
   * place so the two cannot be configured differently. Read once when the
   * shadow uniforms are pushed; none of it changes after construction except
   * time, which the caller already has.
   */
  readonly shadowParams: {
    deckRadius: number;
    coverage: number;
    drift: number;
    banded: number;
  };

  /**
   * @param seaLevelRadius waterline radius, metres; 0 on a dry world
   */
  constructor(body: CelestialBody, quality: number, seaLevelRadius = 0) {
    const atm = body.atmosphere;

    // Cloud altitude follows the authored weather rather than the terrain
    // ceiling. The slab has real depth and real fragment depth now, so peaks
    // can enter or occlude it naturally without forcing the entire deck above
    // the highest theoretical mountain. Keep only a waterline clearance to
    // avoid coincident ocean/cloud surfaces on unusual worlds.
    const waterline = Math.max(0, seaLevelRadius - body.radius);
    // A kilometre-thin slab can only read as fog. Give temperate decks enough
    // vertical room to form recognisable multi-kilometre cumulus towers while
    // keeping the authored centre altitude and the same global coverage map.
    const nominalThickness = Math.max(1800, Math.min(6500, atm.cloudAltitude * 0.9));
    const centreAltitude = Math.max(500, atm.cloudAltitude, waterline + 900);
    const outerRadius = body.radius + centreAltitude + nominalThickness * 0.5;
    // The solid body the deck can hide behind: the waterline on an ocean world
    // (which can sit either side of the datum — see `solidOccluderDistance`),
    // the datum itself otherwise.
    const occluderRadius = Math.max(body.radius, seaLevelRadius);
    // A thick deck centred just above the waterline can otherwise put its own
    // floor under the sea, which both wastes march samples on submerged air and
    // leaves `height01` measured from a surface that is not there. Clamped
    // here, once, so the shader's normalising thickness is the real one.
    const innerRadius = Math.max(occluderRadius + MIN_INNER_CLEARANCE, outerRadius - nominalThickness);
    const thickness = Math.max(1, outerRadius - innerRadius);
    // Keep the raster proxy immediately outside the real slab. The fragment
    // shader now writes/tests the real cloud entry depth, so a higher proxy no
    // longer helps mountain occlusion; it only assigns a nearby cloud to a
    // different depth camera near the horizon, leaving a broad clear band at
    // the pass boundary. Peaks above the slab correctly occlude it like any
    // other foreground geometry.
    this.proxyRadius = outerRadius + 100;

    this.uniforms = {
      uPlanetCentre: { value: new THREE.Vector3() },
      uCameraFixed: { value: new THREE.Vector3() },
      // The occluder is the datum, not the highest terrain: erring small here
      // can only leave a sliver of cloud visible past a mountain, while erring
      // large would punch holes in the deck over low ground.
      uPlanetRadius: { value: body.radius },
      uOccluderRadius: { value: occluderRadius },
      uInnerRadius: { value: innerRadius },
      uProxyRadius: { value: this.proxyRadius },
      uSunDirection: { value: new THREE.Vector3(1, 0, 0) },
      uSunColor: { value: new THREE.Color(...SUN_COLOR) },
      uSunIntensity: { value: SUN_INTENSITY_DEFAULT },
      uCloudColor: {
        value: new THREE.Color(atm.cloudColor[0], atm.cloudColor[1], atm.cloudColor[2]),
      },
      uCoverage: { value: atm.cloudCoverage },
      uTime: { value: 0 },
      // Angular speed of the authored wind. Both the global density map and
      // metre-scale volume convert this same value back to their own units, so
      // descending never swaps between fields moving at unrelated speeds.
      uDrift: { value: atm.windSpeed / Math.max(1, body.radius) },
      uBanded: { value: body.def.kind === 'gasGiant' ? 1 : 0 },
      uLayerFade: { value: 1 },
      uDeckRadius: { value: outerRadius },
      // Real depth for the near-field march. Flat rather than scaled to the
      // deck's clearance: the clearance already varies a great deal between
      // worlds for reasons that have nothing to do with how thick the weather
      // itself should look.
      uDeckThickness: { value: thickness },
      // Sample count for the slab march. The expensive macro field is sampled
      // once regardless (see the fragment shader); this only prices the cheap
      // per-step detail term, so it can afford to scale with quality more
      // freely than a full volumetric march would.
      uMarchSteps: {
        value: quality >= 3 ? MARCH_STEPS_HIGH : quality >= 2 ? MARCH_STEPS_MEDIUM : MARCH_STEPS_LOW,
      },
      // Same atmosphere parameters TerrainMaterial's aerial perspective reads
      // off this body, so the deck hazes with distance exactly like the
      // ground it floats above rather than reading as a separate, airless
      // object.
      uScaleHeight: { value: Math.max(MIN_SCALE_HEIGHT, atm.scaleHeight) },
      uRayleigh: { value: new THREE.Vector3(atm.rayleigh[0], atm.rayleigh[1], atm.rayleigh[2]) },
      uMie: { value: atm.mie },
      uHazeColor: { value: new THREE.Color(atm.hazeColor[0], atm.hazeColor[1], atm.hazeColor[2]) },
      uSurfaceDensity: { value: atm.surfaceDensity },
      uCameraAltitude: { value: 0 },
      uCameraCloudMacro: { value: 0 },
      uCameraCloudDensity: { value: 0 },
      uPassMin: { value: 0 },
      uPassMax: { value: 1e30 },
      uPassCameraNear: { value: 0.1 },
      uPassCameraFar: { value: 1e30 },
      uCloudProjection: { value: new THREE.Matrix4() },
      uRingNormal: { value: new THREE.Vector3(body.spinAxis.x, body.spinAxis.y, body.spinAxis.z) },
      uRingInnerRadius: { value: body.def.rings?.innerRadius ?? 0 },
      uRingOuterRadius: { value: body.def.rings?.outerRadius ?? 0 },
      uRingOpacity: { value: body.def.rings?.opacity ?? 0 },
    };

    const segments = quality >= 3 ? 192 : quality >= 2 ? 128 : 96;
    let interiorMesh: THREE.Mesh | null = null;
    if (body.def.kind === 'gasGiant') {
      const interiorMaterial = new THREE.ShaderMaterial({
        uniforms: {
          uSunDirection: this.uniforms.uSunDirection,
          uSunColor: this.uniforms.uSunColor,
          uSunIntensity: this.uniforms.uSunIntensity,
          uPlanetRadius: this.uniforms.uPlanetRadius,
          uRingNormal: this.uniforms.uRingNormal,
          uRingInnerRadius: this.uniforms.uRingInnerRadius,
          uRingOuterRadius: this.uniforms.uRingOuterRadius,
          uRingOpacity: this.uniforms.uRingOpacity,
          // Darker than the upper ammonia deck: this is the gas revealed by a
          // weather gap, not a second equally bright cloud layer.
          uDeepColor: {
            value: new THREE.Color(atm.cloudColor[0], atm.cloudColor[1], atm.cloudColor[2]).multiplyScalar(0.32),
          },
        },
        vertexShader: GAS_GIANT_INTERIOR_VERTEX,
        fragmentShader: GAS_GIANT_INTERIOR_FRAGMENT,
        transparent: false,
        depthWrite: true,
        depthTest: true,
      });
      interiorMesh = new THREE.Mesh(
        new THREE.SphereGeometry(body.radius, segments, segments / 2),
        interiorMaterial,
      );
      interiorMesh.name = 'gas-giant-interior';
      interiorMesh.frustumCulled = false;
      interiorMesh.renderOrder = 100;
      interiorMesh.layers.set(RENDER_LAYER.clouds);
      this.root.add(interiorMesh);
    }
    this.interiorMesh = interiorMesh;

    let ringSystem: PlanetRingSystem | null = null;
    if (body.def.rings) {
      ringSystem = new PlanetRingSystem(
        body,
        quality >= 3 ? 384 : quality >= 2 ? 256 : 192,
        outerRadius,
      );
      // RingGeometry is generated in XY (normal +Z); the body's fixed frame has
      // +Y as its spin axis, so rotate the sheet into the local equatorial XZ
      // plane. The root supplies axial tilt and planetary rotation in update().
      ringSystem.mesh.rotation.x = Math.PI / 2;
      // Draw after the cloud deck so orbit sees the near-side ring over the
      // weather behind it. From below the deck, the ring shader samples the
      // same global cloud field and reduces its own alpha, revealing the cloud
      // already composited underneath without forcing an opaque depth write.
      ringSystem.mesh.renderOrder = 120;
      this.root.add(ringSystem.mesh);
    }
    this.ringSystem = ringSystem;

    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms as unknown as Record<string, THREE.IUniform>,
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      // Both sides: overhead from the ground, underfoot from orbit.
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
    });

    this.shadowParams = {
      deckRadius: outerRadius - thickness * 0.5,
      coverage: atm.cloudCoverage,
      drift: this.uniforms.uDrift.value,
      banded: this.uniforms.uBanded.value,
    };

    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(this.proxyRadius, segments, segments / 2), material);
    this.mesh.name = 'cloud-deck';
    this.mesh.onBeforeRender = (_renderer, _scene, camera) => {
      this.uniforms.uPassMin.value = Number(camera.userData.cloudPassMin ?? 0);
      this.uniforms.uPassMax.value = Number(camera.userData.cloudPassMax ?? 1e30);
      this.uniforms.uPassCameraNear.value =
        camera instanceof THREE.PerspectiveCamera ? camera.near : 0.1;
      this.uniforms.uPassCameraFar.value =
        camera instanceof THREE.PerspectiveCamera ? camera.far : 1e30;
      this.uniforms.uCloudProjection.value.copy(camera.projectionMatrix);
    };
    this.mesh.frustumCulled = false;
    // Composite after the atmosphere shell. This shader already applies the
    // air column between viewer and cloud; drawing the shell afterwards hid
    // every far-pass cloud behind its nearly opaque horizon haze, leaving the
    // unmistakable clear band at the mid/far depth boundary.
    this.mesh.renderOrder = 110;
    this.mesh.layers.set(RENDER_LAYER.clouds);
    this.root.add(this.mesh);
  }

  /**
   * @param planetCentreRender planet centre relative to the camera, metres
   * @param fixedToInertial rotation from planet-fixed axes into inertial axes
   */
  update(planetCentreRender: Vec3, fixedToInertial: Quat, time: number): void {
    this.root.position.set(planetCentreRender.x, planetCentreRender.y, planetCentreRender.z);
    this.root.quaternion.set(
      fixedToInertial.x,
      fixedToInertial.y,
      fixedToInertial.z,
      fixedToInertial.w,
    );
    this.uniforms.uPlanetCentre.value.set(planetCentreRender.x, planetCentreRender.y, planetCentreRender.z);
    const cameraInertial = planetCentreRender.clone().negate();
    const cameraFixed = fixedToInertial.rotateInverse(cameraInertial);
    this.uniforms.uCameraFixed.value.set(cameraFixed.x, cameraFixed.y, cameraFixed.z);
    this.uniforms.uTime.value = time;

    // Sample the exact global weather field once at the observer. The fragment
    // shader uses this scalar to replace a nearby hard cloud body with local
    // extinction only when the camera is genuinely inside that same body.
    // Clear holes in the global map therefore remain clear while crossing the
    // nominal altitude band.
    const cameraRadius = cameraFixed.length();
    const deckRadius = this.uniforms.uDeckRadius.value;
    const deckThickness = this.uniforms.uDeckThickness.value;
    const innerRadius = this.uniforms.uInnerRadius.value;
    let cameraCloudMacro = 0;
    let cameraCloudDensity = 0;
    if (cameraRadius > innerRadius && cameraRadius < deckRadius) {
      const drift = this.uniforms.uDrift.value;
      const weather = globalCloudDensityAt(
        cameraFixed.normalized(),
        time,
        drift,
        this.uniforms.uCoverage.value,
        this.uniforms.uBanded.value,
      );
      cameraCloudMacro = weather.density;
      const weatherTravel = time * drift * this.uniforms.uPlanetRadius.value;
      const fine = cloudFbm(
        (cameraFixed.x + weatherTravel) / 7200,
        cameraFixed.y / 7200,
        (cameraFixed.z + weatherTravel * 0.6) / 7200,
        4,
      );
      const height01 = clampUnit((cameraRadius - innerRadius) / Math.max(1, deckThickness));
      // Keep in sync with TOWER_TOP_MAX / VERTICAL_EDGE_SOFTNESS and the
      // towerTop formula in the FRAGMENT shader's march loops.
      const towerTop = Math.max(
        0.30,
        Math.min(0.90, 0.50 + weather.density * 0.46 + (fine - 0.5) * 0.30),
      );
      const vertical = smoothstepNumber(0, 0.30, height01)
        * (1 - smoothstepNumber(towerTop - 0.30, towerTop, height01));
      const lobeScale = cloudLobeScaleAt(
        cameraFixed.x,
        cameraFixed.y,
        cameraFixed.z,
        weatherTravel,
        weatherTravel * 0.6,
      );
      const erosionTerm = cloudErosionAt(
        cameraFixed.x,
        cameraFixed.y,
        cameraFixed.z,
        weatherTravel,
        weatherTravel * 0.6,
        weather.density,
        lobeScale,
      );
      cameraCloudDensity = clampUnit(
        weather.density * mixNumber(0.02, 1.25, fine) + erosionTerm * 0.95,
      ) * vertical;
    }
    this.uniforms.uCameraCloudMacro.value = cameraCloudMacro;
    this.uniforms.uCameraCloudDensity.value = cameraCloudDensity;

    // Rendering both faces makes an exterior ray march the same near-side
    // volume twice. Select the one proxy face the camera can actually see.
    const insideProxy = planetCentreRender.length() < this.proxyRadius;
    if (insideProxy !== this.insideProxy) {
      this.insideProxy = insideProxy;
      const material = this.mesh.material as THREE.ShaderMaterial;
      material.side = insideProxy ? THREE.BackSide : THREE.FrontSide;
      material.needsUpdate = true;
    }
  }

  /** Cross-fade with the impostor, which draws its own clouds. See GLSL_DITHER. */
  setSurfaceFade(fade: number): void {
    this.uniforms.uLayerFade.value = fade;
  }

  setLighting(sunDirection: Vec3, sunColor: THREE.Color, intensity: number, cameraAltitude: number): void {
    this.uniforms.uSunDirection.value.set(sunDirection.x, sunDirection.y, sunDirection.z);
    this.uniforms.uSunColor.value.copy(sunColor);
    this.uniforms.uSunIntensity.value = intensity;
    this.uniforms.uCameraAltitude.value = cameraAltitude;
    this.ringSystem?.setLighting(sunDirection, sunColor, intensity);
  }

  dispose(): void {
    this.ringSystem?.dispose();
    if (this.interiorMesh) {
      this.interiorMesh.geometry.dispose();
      (this.interiorMesh.material as THREE.Material).dispose();
      this.root.remove(this.interiorMesh);
    }
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.root.remove(this.mesh);
  }
}
